import { useEffect, useState } from 'react';
import { ReactionResponse } from 'belo-js-sdk';
import { MessageContextValue, useMessageContext } from '../../../context';
import { DefaultBeloChatGenerics } from '../../../types/types';

export interface FetchReactionsOptions<
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics
> {
  shouldFetch: boolean;
  handleFetchReactions?: MessageContextValue<BeloChatGenerics>['handleFetchReactions'];
}

export function useFetchReactions<
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics
>(options: FetchReactionsOptions) {
  const { handleFetchReactions: contextHandleFetchReactions } = useMessageContext<BeloChatGenerics>(
    'useFetchReactions',
  );
  const [reactions, setReactions] = useState<ReactionResponse[]>([]);
  const { handleFetchReactions: propHandleFetchReactions, shouldFetch } = options;
  const [isLoading, setIsLoading] = useState(shouldFetch);
  const handleFetchReactions = propHandleFetchReactions ?? contextHandleFetchReactions;

  useEffect(() => {
    if (!shouldFetch) {
      return;
    }

    let cancel = false;

    (async () => {
      try {
        setIsLoading(true);
        const reactions = await handleFetchReactions();

        if (!cancel) {
          setReactions(reactions);
        }
      } catch (e) {
        if (!cancel) {
          setReactions([]);
        }
      } finally {
        if (!cancel) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancel = true;
    };
  }, [handleFetchReactions, shouldFetch]);

  return { isLoading, reactions };
}
